<template>
  <div ref="mapContainer" class="custom-map-container"></div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export default {
  name: 'aedMap',
  props: {
    center: { type: Object, default: () => ({ lat: 48.2082, lng: 16.3738 }) },
    zoom: { type: Number, default: 8 },
    markers: { type: Array, default: () => [] },
    autoOpenPopup: { type: Boolean, default: false }
  },
  data() {
    return { map: null, leafletMarkers: [], polyline: null };
  },
  mounted() {
    this.initMap();
  },
  beforeDestroy() {
    if (this.map) this.map.remove();
  },
  methods: {
    initMap() {
      this.map = L.map(this.$refs.mapContainer).setView([this.center.lat, this.center.lng], this.zoom);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(this.map);

      this.drawLayers();
    },

    drawLayers() {
      this.leafletMarkers.forEach(m => this.map.removeLayer(m));
      this.leafletMarkers = [];
      if (this.polyline) this.map.removeLayer(this.polyline);

      const coordinates = [];
      const totalMarkers = this.markers.length;
      const markersToOpen = [];

      this.markers.forEach((m, index) => {
        if (m.gps && m.gps.lat && m.gps.lng) {
          const latLng = [m.gps.lat, m.gps.lng];
          coordinates.push(latLng);

          let pinColor = '#3498db';
          if (m.color) {
            pinColor = m.color;
          } else if (totalMarkers > 1) {
            if (index === 0) pinColor = '#2ecc71';
            else if (index === totalMarkers - 1) pinColor = '#e74c3c';
          }

          const hasNumber = m.number !== undefined && m.number !== '';
          const innerContent = hasNumber
            ? `<text x="16" y="14" fill="white" font-size="11" font-family="Arial, sans-serif" font-weight="bold" text-anchor="middle" dominant-baseline="central">${m.number}</text>`
            : `<circle cx="16" cy="14" r="4" fill="white" />`;

          const customIcon = L.divIcon({
            className: 'custom-svg-pin-wrapper',
            html: `
              <svg width="32" height="38" viewBox="0 0 32 38" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0px 2px 4px rgba(0,0,0,0.3));">
                <path d="M16 2C9.4 2 4 7.4 4 14c0 9 12 22 12 22s12-13 12-22c0-6.6-5.4-12-12-12z" fill="${pinColor}" stroke="white" stroke-width="2"/>
                ${innerContent}
              </svg>
            `,
            iconSize: [32, 38],
            iconAnchor: [16, 36],
            popupAnchor: [0, -36] 
          });

          const marker = L.marker(latLng, { icon: customIcon }).addTo(this.map);
          
          if (m.content) {
            const popupOptions = this.autoOpenPopup 
              ? { autoClose: false, closeOnClick: false } 
              : {};
              
            marker.bindPopup(m.content, popupOptions);
            
            if (this.autoOpenPopup) {
              markersToOpen.push(marker);
            }
          }
          
          marker.on('click', () => {
            this.$emit('marker-click', m.gps);
          });

          this.leafletMarkers.push(marker);
        }
      });

      if (coordinates.length > 1) {
        this.polyline = L.polyline(coordinates, { color: '#3388ff', weight: 4, opacity: 0.8 }).addTo(this.map);
      }

      // Grobe Vor-Ausrichtung beim ersten Rendern
      if (coordinates.length === 1) {
        this.map.setView(coordinates[0], 14);
      } else if (coordinates.length > 1) {
        const bounds = L.latLngBounds(coordinates);
        this.map.fitBounds(bounds, { padding: [50, 50] });
      }

    
      // 350ms gibt dem Vuetify-Dialog genügend Zeit, die Öffnen-Animation voll abzuschließen
      setTimeout(() => {
        if (this.map) {
          // 1. Leaflet zwingen, die echten Container-Ausmaße jetzt neu einzulesen
          this.map.invalidateSize();

          // 2. Da sich die Kartengröße geändert hat, zentrieren wir die Route/Marker final neu
          if (coordinates.length === 1) {
            this.map.setView(coordinates[0], 14, { animate: false });
          } else if (coordinates.length > 1) {
            const bounds = L.latLngBounds(coordinates);
            this.map.fitBounds(bounds, { padding: [50, 50], animate: false });
          }

          // 3. Erst jetzt, wo alles perfekt sitzt, sprengen wir ggf. die Popups auf
          if (markersToOpen.length > 0) {
            markersToOpen.forEach(marker => {
              if (this.map.hasLayer(marker)) {
                marker.openPopup();
              }
            });
          }
        }
      }, 350); 
    }
  },
  watch: {
    markers: {
      deep: true,
      handler() { if (this.map) this.drawLayers(); }
    },
    center: {
      deep: true,
      handler(newCenter) {
        if (this.map && newCenter) {
          this.map.flyTo([newCenter.lat, newCenter.lng], 14);
        }
      }
    }
  }
};
</script>

<style>
.custom-map-container { width: 100%; height: 400px; border-radius: 8px; border: 1px solid #ddd; }
.custom-svg-pin-wrapper { background: none !important; border: none !important; }
</style>